import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';

const API_BASE_URL = `${API_BASE}/api`;

/**
 * Normalize API response to ensure consistent GeoJSON Feature structure
 */
const normalizeFeature = (feature) => {
  if (!feature) return null;
  
  // If it's already a proper GeoJSON Feature, return it
  if (feature.type === 'Feature' && feature.geometry && feature.properties) {
    return feature;
  }
  
  // Handle wrapped responses like { run: Feature } or { route: Feature }
  if (feature.run && feature.run.type === 'Feature') {
    return feature.run;
  }
  if (feature.route && feature.route.type === 'Feature') {
    return feature.route;
  }
  
  console.warn('Invalid feature structure:', feature);
  return null;
};

/**
 * Normalize API response to ensure array of GeoJSON Features
 */
const normalizeFeatureCollection = (data) => {
  if (!data) return [];
  
  // If it's a FeatureCollection, extract features
  if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
    return data.features.filter(f => f && f.type === 'Feature');
  }
  
  // If it's already an array, validate each item
  if (Array.isArray(data)) {
    return data
      .map(normalizeFeature)
      .filter(f => f !== null);
  }
  
  console.warn('Invalid feature collection:', data);
  return [];
};

export const useRouteData = () => {
  const [sopRoutes, setSopRoutes] = useState([]);
  const [vesselRuns, setVesselRuns] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [selectedSopRoute, setSelectedSopRoute] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search / filter state
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResultCount, setSearchResultCount] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);

  // Fetch SOP routes on mount
  useEffect(() => {
    const fetchSopRoutes = async () => {
      try {
        console.log('[useRouteData] Fetching SOP routes...');
        const response = await fetch(`${API_BASE_URL}/sop-routes`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch SOP routes`);
        }
        
        const data = await response.json();
        console.log('[useRouteData] Raw SOP routes response:', data);
        
        const normalized = normalizeFeatureCollection(data);
        console.log('[useRouteData] Normalized SOP routes:', normalized);
        
        setSopRoutes(normalized);
        setError(null);
      } catch (err) {
        const errorMsg = err.message || 'Failed to fetch SOP routes';
        setError(errorMsg);
        console.error('[useRouteData] Error fetching SOP routes:', err);
        setSopRoutes([]);
      }
    };

    fetchSopRoutes();
  }, []);

  // Fetch vessels list on mount
  useEffect(() => {
    const fetchVessels = async () => {
      try {
        console.log('[useRouteData] Fetching vessels...');
        const response = await fetch(`${API_BASE_URL}/vessels`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch vessels`);
        }
        
        const data = await response.json();
        console.log('[useRouteData] Raw vessels response:', data);
        
        // Ensure vessels is always an array
        const vesselsList = Array.isArray(data) ? data : [];
        setVessels(vesselsList);
        setError(null);
      } catch (err) {
        const errorMsg = err.message || 'Failed to fetch vessels';
        setError(errorMsg);
        console.error('[useRouteData] Error fetching vessels:', err);
        setVessels([]);
      }
    };

    fetchVessels();
  }, []);

  // Fetch vessel runs when vessel is selected
  useEffect(() => {
    const fetchRuns = async () => {
      if (!selectedVessel) {
        console.log('[useRouteData] No vessel selected, clearing runs');
        setVesselRuns([]);
        setSelectedRun(null);
        setLoading(false);
        setIsFiltered(false);
        setSearchResultCount(null);
        return;
      }

      try {
        console.log('[useRouteData] Fetching runs for vessel:', selectedVessel);
        setLoading(true);
        
        const response = await fetch(
          `${API_BASE_URL}/runs?vessel=${encodeURIComponent(selectedVessel)}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch runs`);
        }
        
        const data = await response.json();
        console.log('[useRouteData] Raw runs response:', data);
        
        const normalized = normalizeFeatureCollection(data);
        console.log('[useRouteData] Normalized runs:', normalized);
        
        setVesselRuns(normalized);
        setIsFiltered(false);
        setSearchResultCount(null);
        setError(null);
      } catch (err) {
        const errorMsg = err.message || 'Failed to fetch runs';
        setError(errorMsg);
        console.error('[useRouteData] Error fetching runs:', err);
        setVesselRuns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRuns();
  }, [selectedVessel]);

  // Search / filter runs via /api/runs/search
  const searchRuns = useCallback(async ({ filters = {}, sortBy = 'start_time', sortDir = 'desc' } = {}) => {
    try {
      console.log('[useRouteData] Searching runs with filters:', filters);
      setSearchLoading(true);
      setError(null);

      const mergedFilters = { ...filters };
      if (selectedVessel) mergedFilters.vessel_name = selectedVessel;

      const params = new URLSearchParams();

      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) return;
        // Arrays (e.g. origin_zone_ids) become repeated params:
        // origin_zone_ids=a&origin_zone_ids=b
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, String(v)));
        } else {
          params.append(key, String(value));
        }
      });

      params.append('sort_by', sortBy);
      params.append('sort_dir', sortDir);
      params.append('limit', '500');

      const url = `${API_BASE_URL}/runs/search?${params.toString()}`;
      console.log('[useRouteData] Search URL:', url);

      const response = await fetch(url);

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}: Search failed`);
      }

      const data = await response.json();
      const normalized = normalizeFeatureCollection(data);

      setVesselRuns(normalized);
      setSearchResultCount(normalized.length);
      setIsFiltered(true);
      setSelectedRun(null);

      return normalized;
    } catch (err) {
      const errorMsg = err.message || 'Search failed';
      setError(errorMsg);
      console.error('[useRouteData] Search error:', err);
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [selectedVessel]);

  // Reset filters � reload unfiltered vessel runs
  const resetRunFilters = useCallback(async () => {
    setIsFiltered(false);
    setSearchResultCount(null);
    setSelectedRun(null);

    if (!selectedVessel) {
      setVesselRuns([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/runs?vessel=${encodeURIComponent(selectedVessel)}`
      );
      if (!response.ok) throw new Error('Failed to reload runs');
      const data = await response.json();
      setVesselRuns(normalizeFeatureCollection(data));
    } catch (err) {
      console.error('[useRouteData] Reset error:', err);
      setVesselRuns([]);
    } finally {
      setLoading(false);
    }
  }, [selectedVessel]);

  // Fetch detailed run data when run is selected
  const fetchRunDetails = async (runId) => {
    try {
      console.log('[useRouteData] Fetching run details for ID:', runId);
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/runs/${runId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch run details`);
      }
      
      const data = await response.json();
      console.log('[useRouteData] Raw run details response:', data);
      
      // Normalize the run feature
      const normalizedRun = normalizeFeature(data);
      console.log('[useRouteData] Normalized run:', normalizedRun);
      
      setSelectedRun(normalizedRun);
      setError(null);
      
      return normalizedRun;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch run details';
      setError(errorMsg);
      console.error('[useRouteData] Error fetching run details:', err);
      setSelectedRun(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch SOP route details
  const fetchSopRouteDetails = async (routeId) => {
    try {
      console.log('[useRouteData] Fetching SOP route details for ID:', routeId);
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/sop-routes/${routeId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch SOP route details`);
      }
      
      const data = await response.json();
      console.log('[useRouteData] Raw SOP route details response:', data);
      
      // Normalize the route feature
      const normalizedRoute = normalizeFeature(data);
      console.log('[useRouteData] Normalized SOP route:', normalizedRoute);
      
      setSelectedSopRoute(normalizedRoute);
      setError(null);
      
      return normalizedRoute;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch SOP route details';
      setError(errorMsg);
      console.error('[useRouteData] Error fetching SOP route details:', err);
      setSelectedSopRoute(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch run metrics
  const fetchRunMetrics = async (runId) => {
    try {
      console.log('[useRouteData] Fetching metrics for run ID:', runId);
      
      const response = await fetch(`${API_BASE_URL}/runs/${runId}/metrics`);
      
      if (!response.ok) {
        // Metrics might not exist yet - this is not an error
        console.log('[useRouteData] No metrics found for run:', runId);
        return null;
      }
      
      const data = await response.json();
      console.log('[useRouteData] Run metrics:', data);
      
      return data || null;
    } catch (err) {
      console.warn('[useRouteData] Error fetching run metrics:', err);
      return null;
    }
  };

  // Custom setter for selectedVessel to clear related state
  const setSelectedVesselSafe = (vessel) => {
    console.log('[useRouteData] Setting selected vessel:', vessel);
    setSelectedVessel(vessel);
    setSelectedRun(null); // Clear selected run when changing vessel
  };

  // Custom setter for selectedSopRoute
  const setSelectedSopRouteSafe = (route) => {
    console.log('[useRouteData] Setting selected SOP route:', route);
    setSelectedSopRoute(route);
  };

  // Custom setter for selectedRun
  const setSelectedRunSafe = (run) => {
    console.log('[useRouteData] Setting selected run:', run);
    setSelectedRun(run);
  };

  return {
    // State - always guaranteed to be defined
    sopRoutes: sopRoutes || [],
    vesselRuns: vesselRuns || [],
    vessels: vessels || [],
    selectedVessel: selectedVessel || null,
    selectedSopRoute: selectedSopRoute || null,
    selectedRun: selectedRun || null,
    loading: loading || false,
    error: error || null,
    
    // Search state
    searchLoading,
    searchResultCount,
    isFiltered,
    
    // Setters
    setSelectedVessel: setSelectedVesselSafe,
    setSelectedSopRoute: setSelectedSopRouteSafe,
    setSelectedRun: setSelectedRunSafe,
    
    // Fetch methods
    fetchRunDetails,
    fetchSopRouteDetails,
    fetchRunMetrics,
    
    // Search methods
    searchRuns,
    resetRunFilters,
  };
};